import React, { FC, useEffect, useState, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { rgba } from 'polished';
import { faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';

import { colorStack } from '../../../styleHelpers/colors';
import { ESpinnerSize, Spinner } from '../Spinner/Spinner';
import { fontSizeAndHeight } from '../../../styleHelpers/fontSizes';
import IntlMessage from '../IntlMessage';

const Wrpper = styled.div`
    border-radius: 8px;
    margin: 0 0 .5rem 0;
`;

const Header = styled.div<{ collapsed?: boolean, inline?: boolean}>`
    background: ${colorStack.white};
    color: ${colorStack.darkBlue};
    font-weight: 500;
    padding: 1.25rem 1rem 1.25rem 1rem;
    border-radius: 8px 8px 0 0;
    position: relative;
    ${props => props.inline && css`
        display: flex;
        justify-content: space-between;
    `};
    &::after {
        display: block;
        border-radius: 20px;
        width: calc(100% - 3rem);
        height: 3px;
        background: ${colorStack.bodyBg};
        content: '';
        position: absolute;
        bottom: 0;
    }
    ${props => props.collapsed && css`
        border-radius: 8px;
        &::after {
            display: none;
        }
    `}
`;

const Inner = styled.div`
    background: ${colorStack.white};
    padding: 1.5rem 2.5rem;
    border-radius: 0 0 8px 8px;
    position: relative;
`;

const Loader = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${rgba(colorStack.black, .1)};
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 99;
`;

const HeaderButton = styled.button`
    outline: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    ${fontSizeAndHeight[20]};
    font-weight: 500;
    color: ${colorStack.content};
    svg {
        color: ${colorStack.ligthGrey};
        ${fontSizeAndHeight[25]}
        margin-right: 1rem;
    }
`;

interface IProps {
    headerId?: string;
    title?: string;
    scrollId?: string;
    nonCollapsable?: boolean;
    isCollapsed?: boolean;
    loading?: boolean;
    inlineHeader?: boolean;
    headerExtraContent?: React.ReactNode;
}

export const Box: FC<React.PropsWithChildren<IProps>> = props => {
    const [collapsed, setCollapsed] = useState<boolean>(false);

    useEffect(() => {
        setCollapsed(props.isCollapsed);
    }, [props.isCollapsed]);

    const onHeaderClick = useCallback(() => {
        setCollapsed(currentState => !currentState);
    }, []);

    return (
        <Wrpper>
            {props.scrollId &&
                <a data-scrollId={props.scrollId} data-scrollName={props.title} />
            }
            <Header inline={props.inlineHeader} collapsed={collapsed}>
                <HeaderButton onClick={!props.nonCollapsable && onHeaderClick} type="button">
                    {!props.nonCollapsable && (
                        <FontAwesomeIcon icon={collapsed ? faCaretDown : faCaretUp} />
                    )}
                    {props.headerId && <IntlMessage id={props.headerId} />}
                    {props.title}
                </HeaderButton>
                {props.headerExtraContent}
            </Header>
            {(!collapsed || props.nonCollapsable) && (
                <Inner>
                    {props.loading &&
                        <Loader>
                            <Spinner size={ESpinnerSize.MEDIUM}/>
                        </Loader>
                    }
                    {props.children}
                </Inner>
            )}
        </Wrpper>
    );
};
